import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePortalDestination } from './PortalDestinationContext';

const Context = createContext(null);
export const PortalUnreadProvider = ({ children }) => {
  const { api } = usePortalDestination();
  const [count, setCount] = useState(0);
  const generation = useRef(0);
  const live = useRef(false);
  const refresh = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;
    const request = ++generation.current;
    try {
      const { data } = await api.get('/chat/unread');
      if (live.current && request === generation.current) setCount(data.unread_messages);
    } catch { /* Preserve last known count; retry on focus, reconnection or next poll. */ }
  }, [api]);
  useEffect(() => {
    live.current = true;
    setCount(0);
    refresh();
    const timer = window.setInterval(refresh, 20000);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    // Broadcast only an invalidation signal, never patient IDs or message data.
    const channel = 'BroadcastChannel' in window ? new BroadcastChannel('portal-chat-read') : null;
    if (channel) channel.onmessage = refresh;
    return () => {
      live.current = false;
      generation.current += 1;
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
      channel?.close();
    };
  }, [refresh]);
  const refreshAfterRead = useCallback(() => {
    refresh();
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('portal-chat-read');
      channel.postMessage('refresh');
      channel.close();
    }
  }, [refresh]);
  return <Context.Provider value={{ count, refreshAfterRead }}>{children}</Context.Provider>;
};

export const usePortalUnread = () => useContext(Context);